import LocationOnIcon from '@mui/icons-material/LocationOn';
import Autocomplete from '@mui/material/Autocomplete';
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import parse from 'autosuggest-highlight/parse';
import { useEffect } from 'react';
import * as React from 'react';

import { AddressComponent, GeocoderResult, MapSearchProps, PlaceType } from './MapSearch.props';
import { SizeProps } from '../../../@types';
import { GOOGLE_MAPS_API_KEY } from '../../../configs/envs';
import { isEmptyString } from '../../../utils/common';
import { TextField } from '../../atoms/TextField';
import { Typography, TypographyVariantProps } from '../../atoms/Typography';

const loadScript = (src: string, position: HTMLElement | null, id: string) => {
  if (!position) return;

  const script = document.createElement('script');
  script.setAttribute('async', '');
  script.setAttribute('id', id);
  script.src = src;
  position.appendChild(script);
};

const autocompleteService: { current: google.maps.places.AutocompleteService | null } = {
  current: null,
};
const geocoder: { current: google.maps.Geocoder | null } = { current: null };

const extractPostalFromDescription = (description: string) => {
  const match = description.match(/\b\d{4,6}\b/); // 4-6 digit postal code
  return match ? match[0] : null;
};

// fetch postal code from geocoder
const fetchPostalCode = (placeId: string) => {
  return new Promise<string | null>((resolve) => {
    if (!geocoder.current) return resolve(null);

    geocoder.current.geocode({ placeId }, (results, status) => {
      if (status !== 'OK' || !results?.[0]) return resolve(null);

      const postal = results[0].address_components.find((c) => c.types.includes('postal_code'));

      resolve(postal ? postal.long_name : null);
    });
  });
};

export const MapSearch: React.FC<MapSearchProps> = ({
  label,
  selectedValue,
  addressData,
  disabled,
  onChange,
  hasError,
  required,
}) => {
  const [value, setValue] = React.useState<PlaceType | null>(null);
  const [inputValue, setInputValue] = React.useState(selectedValue ? selectedValue : '');
  const [options, setOptions] = React.useState<readonly PlaceType[]>([]);
  const loaded = React.useRef(false);

  if (typeof window !== 'undefined' && !loaded.current) {
    if (!document.querySelector('#google-maps')) {
      loadScript(
        `https://maps.googleapis.com/maps/api/js?key=${GOOGLE_MAPS_API_KEY}&libraries=places,geocoding`,
        document.querySelector('head'),
        'google-maps'
      );
    }
    loaded.current = true;
  }

  const fetch = React.useCallback(
    (request: { input: string }, callback: (results?: readonly PlaceType[]) => void) => {
      autocompleteService.current?.getPlacePredictions(
        { ...request, componentRestrictions: { country: 'au' } },
        (predictions) => callback(predictions ?? undefined)
      );
    },
    []
  );

  const fillAddressFields = (components: AddressComponent[]) => {
    let fillAddress = {
      line1: '',
      suburb: '',
      city: '',
      stateName: '',
      countryName: '',
      postCode: '',
    };
    components.map(({ longName, types }: AddressComponent) => {
      if (types.includes('subpremise')) {
        fillAddress = { ...fillAddress, line1: longName };
      } else if (types.includes('street_number')) {
        fillAddress = {
          ...fillAddress,
          line1: `${fillAddress.line1 && fillAddress.line1.concat('/')}${longName}`,
        };
      } else if (types.includes('route')) {
        fillAddress = {
          ...fillAddress,
          line1: `${fillAddress.line1 && fillAddress.line1.concat(' ')}${longName}`,
        };
      } else if (types.includes('sublocality')) {
        fillAddress = { ...fillAddress, suburb: longName };
      } else if (types.includes('locality')) {
        if (isEmptyString(fillAddress.suburb)) fillAddress = { ...fillAddress, suburb: longName };
        else fillAddress = { ...fillAddress, city: longName };
      } else if (types.includes('administrative_area_level_2')) {
        const countryValidation =
          components.find((field) => field.types.includes('country'))?.longName !== 'Australia';
        if (isEmptyString(fillAddress.city) && countryValidation)
          fillAddress = { ...fillAddress, city: longName };
      } else if (types.includes('administrative_area_level_1')) {
        fillAddress = { ...fillAddress, stateName: longName };
      } else if (types.includes('country')) {
        fillAddress = { ...fillAddress, countryName: longName };
      } else if (types.includes('postal_code')) {
        fillAddress = { ...fillAddress, postCode: longName };
      }
    });
    return fillAddress;
  };

  useEffect(() => {
    let active = true;

    if (!autocompleteService.current && typeof google !== 'undefined') {
      autocompleteService.current = new google.maps.places.AutocompleteService();
      geocoder.current = new google.maps.Geocoder();
    }
    if (!autocompleteService.current) return;

    if (inputValue === '') {
      setOptions(value ? [value] : []);
      return undefined;
    }

    fetch({ input: inputValue }, async (results?: readonly PlaceType[]) => {
      if (!active) return;

      let newOptions: readonly PlaceType[] = [];
      if (value) newOptions = [value];

      if (results) {
        const enriched = await Promise.all(
          results.map(async (item) => {
            let postalCode = extractPostalFromDescription(item.description);
            if (!postalCode) {
              postalCode = await fetchPostalCode(item.place_id);
            }
            return { ...item, postalCode };
          })
        );

        newOptions = [...newOptions, ...enriched];
      }

      if (selectedValue === newOptions[0]?.description) {
        setValue(newOptions[0]);
      }

      setOptions(newOptions);
    });

    return () => {
      active = false;
    };
  }, [value, inputValue, fetch, selectedValue]);

  // Autocomplete value on initial load. Skip when the prop already matches the
  // current value — without this guard, every parent re-render that echoes our
  // own dispatch (selectedValue ← onChange ← geocoder) would mint a new value
  // object, which would re-fire onChange via the autocomplete onChange path
  // and wedge React in an update loop (#185).
  useEffect(() => {
    if (!selectedValue) return;
    if (value?.description === selectedValue) return;

    const presetOption = {
      description: selectedValue,
      place_id: '',
      structured_formatting: {
        main_text: selectedValue,
        secondary_text: selectedValue,
        main_text_matched_substrings: [],
      },
    };
    setValue(presetOption);
    setOptions([presetOption]);
    setInputValue(selectedValue);
  }, [selectedValue]);

  return (
    // The list below is pinned to this wrapper, which hugs the field exactly.
    <Box sx={{ position: 'relative', width: '100%' }}>
      <Autocomplete
        id="google-map-demo"
        disabled={disabled}
        // The options list is pinned to the field by CSS rather than measured.
        //
        // `MainLayout`'s `OutletWrapper` carries `zoom: 1.1`. A portalled Popper
        // reads its anchor with `getBoundingClientRect` — post-zoom — but then
        // positions itself in `document.body`'s unzoomed space, so the list
        // landed at 1/1.1 of where and how wide it belonged: visibly offset
        // from the field and 9% too narrow. Keeping it inside the zoomed
        // subtree with `disablePortal` removes the coordinate-space mismatch,
        // and pinning it to the wrapper below in percentages leaves no
        // measurement to get wrong.
        //
        // Both hosts (the structure address panel, the service location panel)
        // let it overflow. A host that clips would need the portal back, and
        // would hit the zoom bug again.
        disablePortal
        slotProps={{
          popper: {
            sx: {
              zIndex: (theme) => theme.zIndex.modal + 1,
              position: 'absolute !important',
              width: '100% !important',
              top: '100% !important',
              left: '0 !important',
              transform: 'none !important',
            },
          },
        }}
        getOptionLabel={(option) => (typeof option === 'string' ? option : option.description)}
        filterOptions={(x) => x}
        options={options}
        autoComplete
        includeInputInList
        filterSelectedOptions
        value={value}
        onChange={(_event, newValue: PlaceType | null) => {
          setOptions(newValue ? [newValue, ...options] : options);
          setValue(newValue);
          if (geocoder.current && newValue?.place_id) {
            geocoder.current.geocode({ placeId: newValue.place_id }, (results, status) => {
              if (status === 'OK' && results?.[0]) {
                const first = results[0];
                const result: GeocoderResult = {
                  formattedAddress: first.formatted_address || '',
                  addressComponents: first.address_components.map((component) => ({
                    longName: component.long_name,
                    shortName: component.short_name,
                    types: [...component.types],
                  })),
                  placeId: first.place_id || '',
                };
                const fillAddress = fillAddressFields(result.addressComponents);
                addressData &&
                  onChange({
                    ...addressData,
                    ...fillAddress,
                    googleAddress: result.formattedAddress,
                    googleResponse: first,
                  });
              }
            });
          }
        }}
        onInputChange={(_event, newInputValue) => {
          setInputValue(newInputValue);
        }}
        renderInput={(params) => (
          <TextField
            {...params}
            disabled={disabled}
            label={label}
            fullWidth
            size={SizeProps.Small}
            error={hasError}
            required={required}
          />
        )}
        renderOption={(props, option) => {
          const enriched = option as PlaceType & { postalCode?: string | null };
          const matches = enriched.structured_formatting.main_text_matched_substrings;

          const parts = parse(
            enriched.structured_formatting.main_text,
            matches?.length
              ? matches.map((match) => [match.offset, match.offset + match.length])
              : []
          );

          let secondaryText = enriched.structured_formatting.secondary_text || '';

          if (enriched.postalCode) {
            const segments = secondaryText.split(',').map((p) => p.trim());
            if (segments.length > 1) {
              segments.splice(segments.length - 1, 0, enriched.postalCode);
            } else {
              segments.push(enriched.postalCode);
            }
            secondaryText = segments.join(', ');
          }

          return (
            <li {...props}>
              <Grid container alignItems="center">
                <Grid>
                  <Box component={LocationOnIcon} sx={{ color: 'text.secondary', mr: 2 }} />
                </Grid>
                <Grid size="grow">
                  {parts.map((part, index) => (
                    <span key={index} style={{ fontWeight: part.highlight ? 700 : 400 }}>
                      {part.text}
                    </span>
                  ))}
                  <Typography variant={TypographyVariantProps.Body2}>{secondaryText}</Typography>
                </Grid>
              </Grid>
            </li>
          );
        }}
      />
    </Box>
  );
};
